/**
 * 考试页面组件
 * 学生答题界面，包含答题和成绩展示
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  HelpCircle,
  AlertCircle,
  Send,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  ListChecks,
  ToggleLeft,
  MessageSquare,
  Loader2,
  Trophy,
  XCircle,
  ArrowLeft,
} from 'lucide-react';
import type { ExamPaperWithQuestions, ExamQuestion, StudentExamTask, ExamSubmitResult, AnswerRecordWithCorrect } from '@/types/api';
import { api } from '@/services/api';
import { useToast } from '@/components/Toast';
import styles from './ExamPage.module.css';

interface ExamPageProps {
  task: StudentExamTask;
  userId: number;
  onComplete: () => void;
  onCancel: () => void;
}

// 题目类型配置
const QUESTION_TYPES = [
  { value: 'single', label: '单选题', icon: ListChecks, color: '#3b82f6' },
  { value: 'multiple', label: '多选题', icon: CheckCircle, color: '#8b5cf6' },
  { value: 'multi', label: '多选题', icon: CheckCircle, color: '#8b5cf6' },
  { value: 'judgment', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'judge', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'boolean', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'essay', label: '简答题', icon: MessageSquare, color: '#10b981' },
  { value: 'short', label: '简答题', icon: MessageSquare, color: '#10b981' },
];

// 判断题类型列表
const JUDGMENT_TYPES = ['judgment', 'judge', 'boolean'];

// 判断是否为判断题
const isJudgmentType = (type: string) => JUDGMENT_TYPES.includes(type);

// 简答题类型列表
const ESSAY_TYPES = ['essay', 'short'];

// 判断是否为简答题
const isEssayType = (type: string) => ESSAY_TYPES.includes(type);

// 获取题目类型配置
const getQuestionTypeConfig = (type: string) => {
  return QUESTION_TYPES.find(t => t.value === type) || QUESTION_TYPES[0];
};

export default function ExamPage({ task, userId, onComplete, onCancel }: ExamPageProps) {
  const { showToast } = useToast();

  const [paper, setPaper] = useState<ExamPaperWithQuestions | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [remainingTime, setRemainingTime] = useState(task.exam_duration ? task.exam_duration * 60 : 0);

  // 考试结果状态
  const [examResult, setExamResult] = useState<ExamSubmitResult | null>(null);
  const [showOnlyWrong, setShowOnlyWrong] = useState(false);

  // 加载试卷
  useEffect(() => {
    const loadPaper = async () => {
      try {
        const res = await api.getTaskPaper(task.exam_task_id, userId);
        if (res.code === 200 && res.data) {
          setPaper(res.data);
          // 初始化答案
          const initialAnswers: Record<number, string> = {};
          res.data.questions.forEach(q => {
            initialAnswers[q.question_id] = '';
          });
          setAnswers(initialAnswers);
          // 设置倒计时
          setRemainingTime((task.exam_duration || res.data.exam_duration) * 60);
        }
      } catch (error) {
        showToast('error', '加载试卷失败');
      } finally {
        setLoading(false);
      }
    };
    loadPaper();
  }, [task.exam_task_id, userId, task.exam_duration, showToast]);

  // 后退拦截 - 防止考试过程中通过浏览器后退离开
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
      showToast('warning', '请先提交试卷才能退出考试');
    };

    // 添加 history state 和事件监听
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showToast]);

  // 倒计时
  useEffect(() => {
    if (remainingTime <= 0 || !paper || examResult) return;

    const timer = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [paper, examResult]);

  // 格式化时间
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 处理答案变化
  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  // 处理多选题答案
  const handleMultipleAnswerToggle = (questionId: number, option: string) => {
    setAnswers(prev => {
      const current = prev[questionId] || '';
      const options = current.split('');

      if (options.includes(option)) {
        return {
          ...prev,
          [questionId]: options.filter(o => o !== option).sort().join(''),
        };
      } else {
        return {
          ...prev,
          [questionId]: [...options, option].sort().join(''),
        };
      }
    });
  };

  // 提交考试
  const handleSubmit = async (isAutoSubmit: boolean = false) => {
    if (!paper) return;

    const unansweredCount = paper.questions.filter(q => !answers[q.question_id]?.trim()).length;

    if (!isAutoSubmit && unansweredCount > 0) {
      setShowConfirm(true);
      return;
    }

    setSubmitting(true);
    try {
      const answerList = Object.entries(answers)
        .filter(([_, answer]) => answer.trim())
        .map(([questionId, answer]) => ({
          question_id: parseInt(questionId),
          student_answer: answer,
        }));

      const res = await api.submitExamAnswers(task.exam_task_id, { answers: answerList }, userId);

      if (res.code === 200 && res.data) {
        setExamResult(res.data);
        showToast('success', '提交成功');
      }
    } catch (error) {
      showToast('error', '提交失败，请重试');
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  // 获取答题进度
  const getProgress = () => {
    if (!paper) return 0;
    const answeredCount = paper.questions.filter(q => answers[q.question_id]?.trim()).length;
    return Math.round((answeredCount / paper.questions.length) * 100);
  };

  // 渲染答题题目
  const renderQuestion = (question: ExamQuestion, index: number) => {
    const typeConfig = getQuestionTypeConfig(question.question_type);
    const currentAnswer = answers[question.question_id] || '';

    return (
      <motion.div
        key={question.question_id}
        className={styles.questionCard}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
      >
        <div className={styles.questionHeader}>
          <div className={styles.questionNumber}>
            <span className={styles.numberBadge}>第 {index + 1} 题</span>
            <span
              className={styles.questionType}
              style={{ backgroundColor: `${typeConfig.color}15`, color: typeConfig.color }}
            >
              <typeConfig.icon size={14} />
              {typeConfig.label}
            </span>
          </div>
          <span className={styles.questionScore}>{question.score} 分</span>
        </div>

        <div className={styles.questionContent}>
          <p>{question.question_content}</p>
        </div>

        {/* 单选题 / 判断题 */}
        {(question.question_type === 'single' || isJudgmentType(question.question_type)) && question.options && (
          <div className={styles.optionsList}>
            {Object.entries(question.options as Record<string, string>)
              .filter(([, value]) => value?.trim())
              .map(([key, value]) => (
                <label
                  key={key}
                  className={`${styles.optionItem} ${currentAnswer === key ? styles.selected : ''}`}
                >
                  <input
                    type="radio"
                    name={`question_${question.question_id}`}
                    checked={currentAnswer === key}
                    onChange={() => handleAnswerChange(question.question_id, key)}
                  />
                  <span className={styles.optionKey}>{key}.</span>
                  <span className={styles.optionValue}>{value}</span>
                </label>
              ))}
          </div>
        )}

        {/* 判断题无选项时自动生成正确/错误选项 */}
        {(question.question_type === 'single' || isJudgmentType(question.question_type)) && !question.options && (
          <div className={styles.optionsList}>
            <label
              className={`${styles.optionItem} ${currentAnswer === 'A' ? styles.selected : ''}`}
            >
              <input
                type="radio"
                name={`question_${question.question_id}`}
                checked={currentAnswer === 'A'}
                onChange={() => handleAnswerChange(question.question_id, 'A')}
              />
              <span className={styles.optionKey}>A.</span>
              <span className={styles.optionValue}>正确</span>
            </label>
            <label
              className={`${styles.optionItem} ${currentAnswer === 'B' ? styles.selected : ''}`}
            >
              <input
                type="radio"
                name={`question_${question.question_id}`}
                checked={currentAnswer === 'B'}
                onChange={() => handleAnswerChange(question.question_id, 'B')}
              />
              <span className={styles.optionKey}>B.</span>
              <span className={styles.optionValue}>错误</span>
            </label>
          </div>
        )}

        {/* 多选题 */}
        {(question.question_type === 'multiple' || question.question_type === 'multi') && question.options && (
          <div className={styles.optionsList}>
            {Object.entries(question.options as Record<string, string>)
              .filter(([, value]) => value?.trim())
              .map(([key, value]) => (
                <label
                  key={key}
                  className={`${styles.optionItem} ${currentAnswer.includes(key) ? styles.selected : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={currentAnswer.includes(key)}
                    onChange={() => handleMultipleAnswerToggle(question.question_id, key)}
                  />
                  <span className={styles.optionKey}>{key}.</span>
                  <span className={styles.optionValue}>{value}</span>
                </label>
              ))}
          </div>
        )}

        {/* 简答题 */}
        {(question.question_type === 'essay' || question.question_type === 'short') && (
          <textarea
            className={styles.essayInput}
            placeholder="请输入您的答案..."
            value={currentAnswer}
            onChange={e => handleAnswerChange(question.question_id, e.target.value)}
            rows={6}
          />
        )}
      </motion.div>
    );
  };

  // 渲染结果题目
  const renderResultQuestion = (record: AnswerRecordWithCorrect, index: number) => {
    const typeConfig = getQuestionTypeConfig(record.question_type);

    return (
      <div
        key={record.question_id}
        className={`${styles.resultQuestionCard} ${record.is_correct ? styles.correctCard : styles.wrongCard}`}
      >
        <div className={styles.questionHeader}>
          <div className={styles.questionNumber}>
            <span className={styles.numberBadge}>第 {index + 1} 题</span>
            <span
              className={styles.questionType}
              style={{ backgroundColor: `${typeConfig.color}15`, color: typeConfig.color }}
            >
              <typeConfig.icon size={14} />
              {typeConfig.label}
            </span>
            {record.is_correct ? (
              <span className={styles.correctBadge}>
                <CheckCircle size={14} /> 正确
              </span>
            ) : (
              <span className={styles.wrongBadge}>
                <XCircle size={14} /> 错误
              </span>
            )}
          </div>
          <span className={styles.questionScore}>
            {record.score} / {record.max_score} 分
          </span>
        </div>

        <div className={styles.questionContent}>
          <p>{record.question_content}</p>
        </div>

        {/* 选项 */}
        {record.options && (
          <div className={styles.optionsList}>
            {Object.entries(record.options as Record<string, string>)
              .filter(([, value]) => value?.trim())
              .map(([key, value]) => {
                const isStudentAnswer = record.student_answer.includes(key);
                const isCorrectAnswer = record.correct_answer.includes(key);
                const isWrong = isStudentAnswer && !isCorrectAnswer;

                let optionClass = styles.optionItem;
                if (isCorrectAnswer) {
                  optionClass = `${styles.optionItem} ${styles.correctOption}`;
                }
                if (isWrong) {
                  optionClass = `${styles.optionItem} ${styles.wrongOption}`;
                }

                return (
                  <div key={key} className={optionClass}>
                    <span className={styles.optionKey}>{key}.</span>
                    <span className={styles.optionValue}>{value}</span>
                    <div className={styles.optionTags}>
                      {isStudentAnswer && (
                        <span className={styles.yourAnswerTag}>
                          <CheckCircle size={12} /> 你的选择
                        </span>
                      )}
                      {isCorrectAnswer && (
                        <span className={styles.correctAnswerTag}>
                          <CheckCircle size={12} /> 正确答案
                        </span>
                      )}
                      {isWrong && (
                        <XCircle size={14} className={styles.wrongIcon} />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* 判断题无选项时自动生成正确/错误选项 */}
        {isJudgmentType(record.question_type) && !record.options && (
          <div className={styles.optionsList}>
            {(['A', 'B'] as const).map((key) => {
              const label = key === 'A' ? '正确' : '错误';
              const isStudentAnswer = record.student_answer === key || record.student_answer === label;
              const isCorrectAnswer = record.correct_answer === label || record.correct_answer === key;
              const isWrong = isStudentAnswer && !isCorrectAnswer;

              let optionClass = styles.optionItem;
              if (isCorrectAnswer) {
                optionClass = `${styles.optionItem} ${styles.correctOption}`;
              }
              if (isWrong) {
                optionClass = `${styles.optionItem} ${styles.wrongOption}`;
              }

              return (
                <div key={key} className={optionClass}>
                  <span className={styles.optionKey}>{key}.</span>
                  <span className={styles.optionValue}>{label}</span>
                  <div className={styles.optionTags}>
                    {isStudentAnswer && (
                      <span className={styles.yourAnswerTag}>
                        <CheckCircle size={12} /> 你的选择
                      </span>
                    )}
                    {isCorrectAnswer && (
                      <span className={styles.correctAnswerTag}>
                        <CheckCircle size={12} /> 正确答案
                      </span>
                    )}
                    {isWrong && (
                      <XCircle size={14} className={styles.wrongIcon} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 简答题显示答案 */}
        {(record.question_type === 'essay' || record.question_type === 'short') && (
          <div className={styles.essayAnswers}>
            <div className={`${styles.essayAnswer} ${record.is_correct ? styles.essayCorrect : styles.essayWrong}`}>
              <span className={styles.answerLabel}>📝 您的答案：</span>
              <p>{record.student_answer || '未作答'}</p>
            </div>
            <div className={`${styles.essayAnswer} ${styles.essayCorrectAnswer}`}>
              <span className={styles.answerLabel}>✅ 参考答案：</span>
              <p>{record.correct_answer}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染考试结果页面
  const renderExamResult = () => {
    if (!examResult) return null;

    const correctCount = examResult.correct_count;
    const wrongCount = examResult.answer_count - correctCount;

    return (
      <div className={styles.resultContainer}>
        {/* 结果头部 */}
        <div className={styles.resultHeader}>
          <button className={styles.backBtn} onClick={onComplete}>
            <ArrowLeft size={16} />
            返回任务列表
          </button>
          <h1 className={styles.resultTitle}>考试结果</h1>
        </div>

        {/* 成绩卡片 */}
        <div className={`${styles.scoreCard} ${examResult.is_passed ? styles.passed : styles.failed}`}>
          <div className={styles.scoreLeft}>
            <div className={styles.scoreCircle}>
              <Trophy size={28} className={styles.trophyIcon} />
              <span className={styles.scoreValue}>{examResult.total_score}</span>
              <span className={styles.scoreTotal}>/{examResult.max_score}</span>
            </div>
            <div className={styles.scoreInfo}>
              <h2>{examResult.is_passed ? '恭喜通过！' : '未通过'}</h2>
              <p>及格线 {examResult.pass_score} 分</p>
            </div>
          </div>
          <div className={styles.scoreRight}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{examResult.answer_count}</span>
              <span className={styles.statLabel}>总题数</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue + ' ' + styles.correctText}>{correctCount}</span>
              <span className={styles.statLabel}>正确</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue + ' ' + styles.wrongText}>{wrongCount}</span>
              <span className={styles.statLabel}>错误</span>
            </div>
          </div>
        </div>

        {/* 题目详情列表 - 可滚动 */}
        <div className={styles.questionsList}>
          <div className={styles.listTitleRow}>
            <h3 className={styles.listTitle}>答题详情</h3>
            <button
              className={`${styles.wrongFilterBtn} ${showOnlyWrong ? styles.wrongFilterActive : ''}`}
              onClick={() => setShowOnlyWrong(prev => !prev)}
            >
              <XCircle size={14} />
              仅看错题
            </button>
          </div>
          <div className={styles.questionsScroll}>
            {examResult.records
              .map((record, index) => ({ record, index }))
              .filter(({ record }) => !showOnlyWrong || !record.is_correct)
              .map(({ record, index }) => renderResultQuestion(record, index))}
            {examResult.records.filter(record => !record.is_correct).length === 0 && showOnlyWrong && (
              <div className={styles.noWrong}>全部正确，没有错题！</div>
            )}
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className={styles.resultFooter}>
          <button className={styles.finishBtn} onClick={onComplete}>
            完成并返回
          </button>
        </div>
      </div>
    );
  };

  // 加载状态
  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader2 size={32} className={styles.spinner} />
        <p>加载试卷中...</p>
      </div>
    );
  }

  // 错误状态
  if (!paper) {
    return (
      <div className={styles.error}>
        <AlertCircle size={48} />
        <p>加载试卷失败</p>
        <button onClick={onCancel} className={styles.backBtn}>返回</button>
      </div>
    );
  }

  // 空题目状态
  if (paper.questions.length === 0) {
    return (
      <div className={styles.error}>
        <AlertCircle size={48} />
        <p>试卷暂无题目，请联系教师添加题目</p>
        <button onClick={onCancel} className={styles.backBtn}>返回</button>
      </div>
    );
  }

  // 考试结果页面
  if (examResult) {
    return renderExamResult();
  }

  // 答题页面
  const currentQuestion = paper.questions[currentIndex];
  const progress = getProgress();

  // 提交中状态显示全屏覆盖层
  if (submitting) {
    return (
      <div className={styles.loading}>
        <Loader2 size={32} className={styles.spinner} />
        <p>{paper?.questions?.some(q => isEssayType(q.question_type)) ? '正在改卷中...' : '提交中...'}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 顶部信息栏 */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{paper.paper_name}</h1>
          <div className={styles.meta}>
            <span><HelpCircle size={14} /> {paper.questions.length} 题</span>
            <span>满分 {paper.total_score} 分</span>
            <span>及格 {paper.pass_score} 分</span>
          </div>
        </div>
        <div className={`${styles.timer} ${remainingTime < 300 ? styles.warning : ''}`}>
          <Clock size={18} />
          <span>{formatTime(remainingTime)}</span>
        </div>
      </div>

      {/* 进度条 */}
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        <span className={styles.progressText}>{progress}% 完成</span>
      </div>

      {/* 主体内容 */}
      <div className={styles.mainContent}>
        {/* 题目导航 */}
        <div className={styles.questionNav}>
          <div className={styles.navHeader}>题目导航</div>
          <div className={styles.navGrid}>
            {paper.questions.map((q, index) => (
              <button
                key={q.question_id}
                className={`${styles.navItem} ${index === currentIndex ? styles.active : ''} ${answers[q.question_id]?.trim() ? styles.answered : ''}`}
                onClick={() => setCurrentIndex(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <div className={styles.navLegend}>
            <span className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.answeredDot}`} />
              已答
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.unansweredDot}`} />
              未答
            </span>
          </div>
        </div>

        {/* 题目区域 */}
        <div className={styles.questionArea}>
          <AnimatePresence mode="wait">
            {renderQuestion(currentQuestion, currentIndex)}
          </AnimatePresence>

          {/* 题目切换 */}
          <div className={styles.questionNavBar}>
            <button
              className={styles.navBtn}
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
            >
              <ChevronLeft size={16} />
              上一题
            </button>
            <span className={styles.questionIndicator}>
              {currentIndex + 1} / {paper.questions.length}
            </span>
            {currentIndex < paper.questions.length - 1 ? (
              <button
                className={styles.navBtn}
                onClick={() => setCurrentIndex(prev => Math.min(paper.questions.length - 1, prev + 1))}
              >
                下一题
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                className={styles.submitBtn}
                onClick={() => handleSubmit()}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    {/* 根据是否有简答题显示不同文案 */}
                    {paper?.questions?.some(q => isEssayType(q.question_type)) ? '正在改卷中...' : '提交中...'}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    提交试卷
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 确认弹窗 */}
      {showConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmModal}>
            <AlertCircle size={48} className={styles.confirmIcon} />
            <h3>确认提交？</h3>
            <p>您还有 <strong>{paper.questions.filter(q => !answers[q.question_id]?.trim()).length}</strong> 道题目未作答</p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelConfirmBtn} onClick={() => setShowConfirm(false)}>
                继续答题
              </button>
              <button className={styles.confirmBtn} onClick={() => { setShowConfirm(false); handleSubmit(true); }}>
                确认提交
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}